// ============================================================================
//  Monthly Medication Dispensing Returns
// ----------------------------------------------------------------------------
//  What was dispensed in a month, per drug, and who received it.
//
//  ── WHY IT IS NOT AN AGE/SEX GRID LIKE ITS SIBLINGS ────────────────────────
//
//  Every other sheet on this screen is a DHIMS fourteen-bracket age/sex table.
//  This one is deliberately a list, and that was the request. A drug is not a
//  morbidity: "how many 15-17 year old males received Paracetamol" is a shape
//  borrowed from disease reporting that answers nothing anyone asks about
//  dispensing, and it would spread 378 drugs across 28 mostly-zero columns.
//
//  It is not a limitation of the data. `medication` keeps its own coarse band —
//  10,696 adult and 3,442 child lines in August 2026 — and both are shown as
//  plain columns. Exact age is reachable too (patient.dob resolves on 14,154 of
//  14,154 rows that month) if the brackets are ever genuinely wanted.
//
//  ── THE THREE THINGS IT DOES ───────────────────────────────────────────────
//
//    1. lists the month's drugs, most-dispensed first
//    2. lets several be picked by name, and shows WHO received each
//    3. prints, on paper anyone can hand over
//
//  The pick is what makes it useful. The month has 378 distinct drugs; nobody
//  wants that on paper. "Show me these four and who got them" is the actual
//  question — a malaria return, an antibiotic audit — and it is one search box
//  and a print button away.
//
//  ── WHY THE DETAIL IS A SECOND REQUEST ─────────────────────────────────────
//
//  The summary is the whole month. Attaching every recipient to every drug
//  would be tens of thousands of rows shipped to draw a list nobody has asked
//  to see. The recipients are fetched only for the drugs actually picked, which
//  is 1,932 rows for three drugs and answers in under a second.
// ============================================================================
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, InputAdornment, Chip, Button, CircularProgress,
  Alert, Checkbox, Tooltip, IconButton,
} from '@mui/material';
import {
  Search as SearchIcon, Print as PrintIcon, Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  monthlyMedicationDispensing, monthlyMedicationDispensingDetail,
} from '../BaseURL';

const GREEN = '#11998e';

const num = (v) => Number(v || 0).toLocaleString();
//  There is no money formatter here, and that is not an omission. The sheet
//  deliberately carries no amounts — a dispensing return is a clinical record of
//  what was given, and a cost column invites it to be read as a pharmacy sales
//  report. The backend no longer selects the column either.

//  The month, spelled out, for the printed heading. `period` is 'YYYY-MM'.
const monthLabel = (period) => {
  const m = /^(\d{4})-(\d{1,2})$/.exec(String(period || ''));
  if (!m) return String(period || '');
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  return `${names[Number(m[2]) - 1]} ${m[1]}`;
};

const th = {
  fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase',
  letterSpacing: 0.4, backgroundColor: '#f1f5f9', whiteSpace: 'nowrap',
};
const td = { fontSize: '0.82rem' };

export default function MonthlyMedicationDispensing({ period }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  //  The drugs chosen, by NAME. A Set keeps the membership test cheap on a list
  //  of 378 and makes "is this one picked" a lookup rather than a scan per row.
  const [picked, setPicked] = useState(() => new Set());

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [openDrug, setOpenDrug] = useState(null);

  const printRef = useRef(null);

  // ── The month's drugs ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!period) return undefined;
    let alive = true;
    setLoading(true); setError(null);
    //  Selections are cleared when the MONTH changes. A drug picked in August
    //  may not have been dispensed at all in July, and carrying the choice over
    //  would print a sheet of empty sections without saying why.
    setPicked(new Set()); setDetail(null); setOpenDrug(null);

    fetch(`${monthlyMedicationDispensing}?period=${encodeURIComponent(period)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        if (!d.success) throw new Error(d.message || 'Could not load the month.');
        setRows(d.data || []);
        setTotals(d.totals || null);
      })
      .catch((e) => { if (alive) { setError(e.message); setRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  // ── Who received the chosen drugs ─────────────────────────────────────────
  const loadDetail = useCallback(async (names) => {
    if (!names.length) { setDetail(null); setTruncated(false); return; }
    setDetailLoading(true); setDetailError(null);
    try {
      //  Joined with a pipe, not a comma: every drug name in this catalogue
      //  contains commas — "Paracetamol Tablet, 500 mg" — and a comma-separated
      //  list would arrive at the server as fragments matching nothing.
      const qs = `period=${encodeURIComponent(period)}`
               + `&drugs=${encodeURIComponent(names.join('|'))}`;
      const r = await fetch(`${monthlyMedicationDispensingDetail}?${qs}`);
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Could not load the recipients.');
      setDetail(d.data || []);
      setTruncated(!!d.truncated);
    } catch (e) {
      setDetailError(e.message); setDetail(null);
    } finally { setDetailLoading(false); }
  }, [period]);

  const pickedList = useMemo(() => [...picked], [picked]);

  const toggle = (name) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  //  Nothing is fetched while the officer is still ticking boxes: the recipients
  //  are loaded when they ask for them, not on every click.
  const populate = () => { loadDetail(pickedList); setOpenDrug(null); };

  const clearAll = () => {
    setPicked(new Set()); setDetail(null); setOpenDrug(null); setTruncated(false);
  };

  // ── The visible list ──────────────────────────────────────────────────────
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name || ''} ${r.code || ''}`.toLowerCase().includes(q));
  }, [rows, search]);

  //  Recipients grouped under their drug, in the order the drugs were chosen so
  //  the printed sheet reads the way the picker does.
  const grouped = useMemo(() => {
    if (!detail) return [];
    const by = new Map();
    for (const r of detail) {
      const k = String(r.name || '').trim();
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(r);
    }
    return pickedList
      .map((n) => ({ name: n, people: by.get(n) || [] }))
      .filter((g) => g.people.length > 0);
  }, [detail, pickedList]);

  /*  ── PRINT ────────────────────────────────────────────────────────────────
   *  A new window holding just the sheet, rather than window.print() over the
   *  whole application. The report sits inside the Health Information shell —
   *  an app bar, a filter bar, a data-set dropdown — and printing the page
   *  prints all of it, with the table broken across whatever the layout does at
   *  paper width. Building the document means the sheet is the only thing on
   *  the paper and its own column widths survive.
   *
   *  Styles are inlined into that document for the same reason: the application
   *  stylesheet is not loaded there, and a print that silently lost its
   *  formatting is worse than one that never offered.                        */
  const printSheet = () => {
    const win = window.open('', '_blank', 'width=1100,height=800');
    if (!win) {
      setDetailError('The browser blocked the print window. Allow pop-ups for this site.');
      return;
    }
    const esc = (v) => String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const summaryRows = (pickedList.length
      ? rows.filter((r) => picked.has(String(r.name).trim()))
      : shown)
      .map((r) => `<tr>
          <td>${esc(r.name)}</td><td class="mono">${esc(r.code || '—')}</td>
          <td class="n">${num(r.patients)}</td><td class="n">${num(r.quantity)}</td>
          <td class="n">${num(r.adult_lines)}</td><td class="n">${num(r.child_lines)}</td>
        </tr>`).join('');

    const detailBlocks = grouped.map((g) => `
      <h3>${esc(g.name)} <span class="sub">${g.people.length} dispensed</span></h3>
      <table>
        <thead><tr>
          <th>#</th><th>Folder no.</th><th>Patient</th><th>Sex</th><th>Date</th>
          <th class="n">Qty</th><th>Frequency</th><th>Route</th>
          <th>Prescriber</th><th>Scheme</th>
        </tr></thead>
        <tbody>${g.people.map((p, i) => `<tr>
          <td class="n">${i + 1}</td>
          <td class="mono">${esc(p.folder_no)}</td>
          <td>${esc(p.patient) || '—'}</td>
          <td>${esc(p.gender) || '—'}</td>
          <td class="mono">${esc(p.given_on)}</td>
          <td class="n">${esc(p.quantity)}</td>
          <td>${esc(p.frequency) || '—'}</td>
          <td>${esc(p.route) || '—'}</td>
          <td>${esc(p.prescriber) || '—'}</td>
          <td>${esc(p.scheme) || '—'}</td>
        </tr>`).join('')}</tbody>
      </table>`).join('');

    win.document.write(`<!doctype html><html><head>
      <meta charset="utf-8"><title>Medication Dispensing — ${esc(monthLabel(period))}</title>
      <style>
        *{box-sizing:border-box}
        body{font:12px/1.45 -apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#111;margin:24px}
        h1{font-size:17px;margin:0 0 2px}
        h2{font-size:13px;margin:22px 0 6px;padding-bottom:3px;border-bottom:2px solid #111}
        h3{font-size:12.5px;margin:16px 0 5px}
        .sub{font-weight:400;color:#555}
        .meta{color:#555;font-size:11px;margin-bottom:14px}
        table{border-collapse:collapse;width:100%;margin-bottom:10px}
        th,td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top}
        th{background:#eee;font-size:10.5px;text-transform:uppercase;letter-spacing:.03em}
        .n{text-align:right;white-space:nowrap}
        .mono{font-family:ui-monospace,Consolas,monospace;white-space:nowrap}
        /* Keep a drug and its recipients together where the paper allows, and
           repeat the header when a long list does have to break. */
        thead{display:table-header-group}
        tr{page-break-inside:avoid}
        h3{page-break-after:avoid}
        @page{size:A4 landscape;margin:12mm}
      </style></head><body>
      <h1>Monthly Medication Dispensing Returns</h1>
      <div class="meta">
        ${esc(monthLabel(period))}
        &nbsp;·&nbsp; printed ${esc(new Date().toLocaleString())}
        ${pickedList.length ? `&nbsp;·&nbsp; ${pickedList.length} medication(s) selected`
          : `&nbsp;·&nbsp; all ${shown.length} medication(s)`}
      </div>
      <h2>Summary</h2>
      <table>
        <thead><tr>
          <th>Medication</th><th>Code</th>
          <th class="n">Patients</th><th class="n">Quantity</th>
          <th class="n">Adult</th><th class="n">Child</th>
        </tr></thead>
        <tbody>${summaryRows}</tbody>
      </table>
      ${detailBlocks ? `<h2>Recipients</h2>${detailBlocks}` : ''}
      </body></html>`);
    win.document.close();
    win.focus();
    //  A tick for the document to lay out; printing an empty one is what a bare
    //  synchronous call gets you in Firefox.
    setTimeout(() => { win.print(); }, 250);
  };

  if (!period) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Choose a period to see what was dispensed.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }} ref={printRef}>
      {/* ── What is in the month ─────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
              Monthly Medication Dispensing Returns
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {monthLabel(period)}
              {totals && ` · ${num(totals.drugs_total)} medications · `
                + `${num(totals.lines_total)} dispensed · `
                + `${num(totals.patients_total)} patients`}
            </Typography>
          </Box>

          <Button variant="contained" startIcon={<PrintIcon />} onClick={printSheet}
            disabled={loading || !rows.length}
            sx={{ bgcolor: GREEN, textTransform: 'none', fontWeight: 700,
              '&:hover': { bgcolor: '#0e7f76' } }}>
            Print / Export
          </Button>
        </Box>
      </Paper>

      {/* ── Choose the medications ───────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
        <TextField
          fullWidth size="small" placeholder="Search a medication by name or code…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }} />

        {pickedList.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            {pickedList.map((n) => (
              <Chip key={n} label={n} size="small" onDelete={() => toggle(n)}
                sx={{ maxWidth: 320 }} />
            ))}
            <Button size="small" onClick={clearAll} sx={{ textTransform: 'none' }}>
              Clear
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" size="small" onClick={populate}
              disabled={detailLoading}
              sx={{ bgcolor: GREEN, textTransform: 'none', fontWeight: 700,
                '&:hover': { bgcolor: '#0e7f76' } }}>
              {detailLoading ? 'Loading…' : `Populate ${pickedList.length} medication(s)`}
            </Button>
          </Box>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {detailError && <Alert severity="warning" sx={{ mb: 2 }}>{detailError}</Alert>}
      {truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only the first 5,000 lines are listed. Narrow the selection for a complete sheet.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : !rows.length ? (
        <Alert severity="info">No medication was dispensed in this period.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={th} />
                <TableCell sx={th}>Medication</TableCell>
                <TableCell sx={th}>Code</TableCell>
                <TableCell sx={th} align="right">Patients</TableCell>
                <TableCell sx={th} align="right">Quantity</TableCell>
                <TableCell sx={th} align="right">Adult</TableCell>
                <TableCell sx={th} align="right">Child</TableCell>
                <TableCell sx={th} align="center">Who</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r) => {
                const name = String(r.name).trim();
                const isPicked = picked.has(name);
                const people = grouped.find((g) => g.name === name)?.people || null;
                const isOpen = openDrug === name;
                return (
                  <React.Fragment key={name}>
                    <TableRow hover selected={isPicked}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={isPicked}
                          onChange={() => toggle(name)}
                          sx={{ color: GREEN, '&.Mui-checked': { color: GREEN } }} />
                      </TableCell>
                      <TableCell sx={{ ...td, fontWeight: isPicked ? 700 : 500 }}>
                        {r.name}
                      </TableCell>
                      <TableCell sx={{ ...td, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {r.code || '—'}
                      </TableCell>
                      <TableCell sx={td} align="right">{num(r.patients)}</TableCell>
                      <TableCell sx={td} align="right">{num(r.quantity)}</TableCell>
                      <TableCell sx={td} align="right">{num(r.adult_lines)}</TableCell>
                      <TableCell sx={td} align="right">{num(r.child_lines)}</TableCell>
                      <TableCell sx={td} align="center">
                        {people ? (
                          <Tooltip title={isOpen ? 'Hide recipients' : 'Show recipients'}>
                            <IconButton size="small"
                              onClick={() => setOpenDrug(isOpen ? null : name)}>
                              {isOpen ? <ExpandLessIcon fontSize="small" />
                                : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    {/*  The recipients, under the drug they were given. Only for a
                        drug that has been populated — the summary is the whole
                        month and nobody has asked to see 14,138 names.  */}
                    {isOpen && people && (
                      <TableRow>
                        {/*  Eight, not ten: checkbox, medication, code, patients,
                             quantity, adult, child, who. It has to match the
                             header exactly or the recipient panel sits under the
                             wrong columns and the table skews. */}
                        <TableCell colSpan={8} sx={{ p: 0, bgcolor: '#f8fafc' }}>
                          <Box sx={{ p: 1.5 }}>
                            <Typography variant="caption"
                              sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
                              {people.length} dispensed
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={th}>Folder no.</TableCell>
                                  <TableCell sx={th}>Patient</TableCell>
                                  <TableCell sx={th}>Sex</TableCell>
                                  <TableCell sx={th}>Date</TableCell>
                                  <TableCell sx={th} align="right">Qty</TableCell>
                                  <TableCell sx={th}>Frequency</TableCell>
                                  <TableCell sx={th}>Route</TableCell>
                                  <TableCell sx={th}>Prescriber</TableCell>
                                  <TableCell sx={th}>Scheme</TableCell>
                                  <TableCell sx={th}>Payment</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {people.map((p, i) => (
                                  <TableRow key={`${p.folder_no}-${p.given_on}-${i}`}>
                                    <TableCell sx={{ ...td, fontFamily: 'monospace' }}>
                                      {p.folder_no}
                                    </TableCell>
                                    <TableCell sx={td}>{p.patient || '—'}</TableCell>
                                    <TableCell sx={td}>{p.gender || '—'}</TableCell>
                                    <TableCell sx={{ ...td, fontFamily: 'monospace' }}>
                                      {p.given_on}
                                    </TableCell>
                                    <TableCell sx={td} align="right">{p.quantity}</TableCell>
                                    <TableCell sx={td}>{p.frequency || '—'}</TableCell>
                                    <TableCell sx={td}>{p.route || '—'}</TableCell>
                                    <TableCell sx={td}>{p.prescriber || '—'}</TableCell>
                                    <TableCell sx={td}>{p.scheme || '—'}</TableCell>
                                    <TableCell sx={td}>{p.payment || '—'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && rows.length > 0 && shown.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No medication in this month matches “{search.trim()}”.
        </Alert>
      )}
    </Box>
  );
}
